import type { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  HostListener,
  inject,
  type OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoInputModule } from '@fino-ui/input';
import { FinoTranslationModule, TranslateService } from '@fino-ui/translation';
import { type PdfJsViewerComponent, PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { CorrectionTableComponent } from './correction-table/correction-table.component';
import {
  type CorrectionResponse,
  CorrectionToolService,
} from './correction-tool.service';

interface ValidationError {
  error: 'INVALID_FIELD_VALUE' | 'MAPPING_ERROR';
  field?: string;
  value?: string;
  expectedType?: string;
  message: string;
}

@Component({
  selector: 'app-correction-tool',
  templateUrl: './correction-tool.component.html',
  styleUrls: ['./correction-tool.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FinoTranslationModule,
    PdfJsViewerModule,
    ButtonModule,
    FinoInputModule,
    FinoCommonModule,
    CorrectionTableComponent,
  ],
})
export class CorrectionToolComponent implements OnInit {
  private readonly correctionService = inject(CorrectionToolService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);

  @ViewChild('pdfViewer', { static: false })
  pdfViewer!: PdfJsViewerComponent;

  public currentDocument = signal<CorrectionResponse | null>(null);

  // UI State
  public threshold = 0.99;

  // PDF State
  public pdfUrl = signal('');
  public currentPageNum = signal(1);
  public totalPages = signal(0);

  leftWidth = 50;
  tableAndDocumentStacked = false;
  isDragging = false;

  documentLoading = signal(false);
  submitting = signal(false);
  reviewSubmitting = signal(false);
  correctionStartedAt: string | null = null;
  showSendToReviewModal = signal(false);
  reviewCommentControl = new FormControl('');

  public loadNextDocumentForm: SubmittableForm = new SubmittableForm({
    loadNext: new FormControl(true),
  });

  get isCurrentCorrectionActionable(): boolean {
    const status = this.currentDocument()?.correction?.status;
    if (!status) return false;

    return !status.toLowerCase().includes('review');
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.loadDocument(params.id);
    });
  }

  growPdfSpace() {
    this.leftWidth += 8;
  }

  shrinkPdfSpace() {
    this.leftWidth -= 8;
  }

  startDragging(event: MouseEvent) {
    this.isDragging = true;
    event.preventDefault();
  }

  @HostListener('document:mouseup')
  stopDragging() {
    this.isDragging = false;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;

    const containerWidth = window.innerWidth;
    const newLeftWidth = (event.clientX / containerWidth) * 100;

    if (newLeftWidth > 10 && newLeftWidth < 90) {
      this.leftWidth = newLeftWidth;
    }
  }

  @HostListener('document:keydown', ['$event'])
  keyboardNavigation(event: KeyboardEvent) {
    const tagName = (event.target as HTMLElement).tagName;
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') return;

    switch (event.key) {
      case 'ArrowLeft':
        this.previousPage();
        break;
      case 'ArrowRight':
        this.nextPage();
        break;
    }
  }

  loadDocument(id?: string) {
    const request = id
      ? this.correctionService.getById(id)
      : this.correctionService.getNext();

    this.documentLoading.set(true);
    request.pipe(finalize(() => this.documentLoading.set(false))).subscribe({
      next: (response) => {
        this.currentDocument.set(response);
        this.correctionStartedAt = new Date().toISOString();
        this.pdfUrl.set('');
        this.totalPages.set(0); // Reset
        this.currentPageNum.set(1); // Reset to page 1
        setTimeout(() => {
          const url = new URL(response.docUrl);
          this.pdfUrl.set(escape(url.pathname + url.search + url.hash));
        }, 100);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.toastr.info(
            this.translate.instant('correctionTool.noMoreDocuments'),
          );
          this.currentDocument.set(null);
          this.pdfUrl.set('');
        } else {
          this.toastr.error(
            this.translate.instant('correctionTool.nextDocument.error'),
          );
        }
      },
    });
  }

  switchPDFTableOrientation() {
    this.tableAndDocumentStacked = !this.tableAndDocumentStacked;
    if (this.tableAndDocumentStacked) {
    }
    this.leftWidth = 50;
  }

  submitCorrection() {
    const doc = this.currentDocument();
    if (!doc) return;
    const correction = doc.correction;
    const data = correction.correctedData ?? correction.extractedData;

    data._extractionMetadata.extractionMethod = 'manual_correction';
    this.submitting.set(true);
    this.correctionService
      .complete(correction._id, data, this.correctionStartedAt)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.currentDocument.set(null);
          this.pdfUrl.set('');

          if (this.loadNextDocumentForm.value.loadNext) {
            this.loadDocument();
          }
          this.toastr.success(
            this.translate.instant('correctionTool.submit.success'),
          );
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 400 && error.error) {
            const validationError = error.error as ValidationError;
            this.toastr.error(validationError.message, undefined, {
              disableTimeOut: true,
              closeButton: true,
            });
          } else {
            this.toastr.error(
              this.translate.instant('correctionTool.submit.error'),
            );
          }
        },
      });
  }

  skipDocument(): void {
    const doc = this.currentDocument();
    if (!doc) return;
    this.submitting.set(true);
    this.correctionService
      .decline(doc.correction._id)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          this.currentDocument.set(null);
          this.pdfUrl.set('');

          if (this.loadNextDocumentForm.value.loadNext) {
            this.loadDocument();
          }
        },
        error: (error) => {
          this.toastr.error(
            this.translate.instant('correctionTool.decline.error') +
              error.message,
          );
        },
      });
  }

  openSendToReviewModal(): void {
    if (!this.currentDocument()) return;
    this.reviewCommentControl.setValue('');
    this.showSendToReviewModal.set(true);
  }

  closeSendToReviewModal(): void {
    if (this.reviewSubmitting()) return;
    this.showSendToReviewModal.set(false);
  }

  confirmSendToReview(): void {
    const doc = this.currentDocument();
    if (!doc || this.reviewSubmitting()) return;

    const correctionId = doc.correction._id;
    const comment = this.reviewCommentControl.value ?? '';

    this.reviewSubmitting.set(true);
    this.correctionService
      .sendToReview(correctionId, comment)
      .pipe(finalize(() => this.reviewSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant('correctionTool.review.success'),
          );
          this.showSendToReviewModal.set(false);
          this.currentDocument.set(null);
          this.pdfUrl.set('');

          if (this.loadNextDocumentForm.value.loadNext) {
            this.loadDocument();
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 304) {
            this.toastr.info(
              this.translate.instant('correctionTool.review.alreadyInReview'),
            );
            this.showSendToReviewModal.set(false);
            this.refreshCurrentCorrectionState(correctionId);
            return;
          }

          if (error.status === 404) {
            this.toastr.error(
              this.translate.instant('correctionTool.review.notFound'),
            );
            this.showSendToReviewModal.set(false);
            this.refreshCurrentCorrectionState();
            return;
          }

          const backendErrorCode = error.error?.error as string | undefined;
          this.toastr.error(
            backendErrorCode ??
              this.translate.instant('correctionTool.review.error'),
          );
        },
      });
  }

  private refreshCurrentCorrectionState(correctionId?: string): void {
    if (correctionId) {
      this.loadDocument(correctionId);
      return;
    }

    this.loadDocument();
  }

  nextPage() {
    if (this.currentPageNum() < this.totalPages()) {
      this.currentPageNum.update((p) => p + 1);
      this.pdfViewer.page = this.currentPageNum();
    }
  }

  previousPage() {
    if (this.currentPageNum() > 1) {
      this.currentPageNum.update((p) => p - 1);
      this.pdfViewer.page = this.currentPageNum();
    }
  }

  onPageInputChange(pageNum: number): void {
    if (pageNum >= 1 && pageNum <= this.totalPages()) {
      this.currentPageNum.set(pageNum);
      this.pdfViewer.page = this.currentPageNum();
    }
  }

  onPdfPageChange(pageNumber: number): void {
    this.currentPageNum.set(pageNumber);
  }

  onPdfLoaded(totalPages: number) {
    this.totalPages.set(totalPages);
    this.pdfViewer.page = 1;
  }
}
